import { useApolloClient } from '@apollo/client';
import type { DocumentNode } from 'graphql';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useUser } from 'context/user-context';
import { GET_INSTALLATION_MONITORING_STORES, GET_INSTALLATION_MONITORING_EVENTS } from 'gql/queries';
import type { MonitoringStoresInput, MonitoringEventsInput } from 'types/installationMonitoring';
import { readMonitoringStores, readMonitoringEvents, validMonitoringInput } from 'utils/installationMonitoring';
import { assistanceUuid } from 'utils/assistedInstallation';
import { boundedInstallationRead, installationAutomaticReadDue } from './useVisibleInstallationRefresh';

function useMonitoringRead<T>(allowed: boolean, actor: string, input: MonitoringStoresInput | MonitoringEventsInput,
  query: DocumentNode, field: string, decode: (value: unknown, input: any) => T) {
  const client = useApolloClient(); const key = JSON.stringify(input); const scope = `${actor}|${allowed}|${key}`;
  const active = useRef({ scope, client }); active.current = { scope, client };
  const mounted = useRef(false), attempted = useRef<number | null>(null);
  const pending = useRef<{ controller: AbortController; promise: Promise<void> } | null>(null);
  const [state, setState] = useState<{ scope: string; client: typeof client; data?: T; loading: boolean; error: string }>({ scope, client, loading: false, error: '' });
  const refresh = useCallback(() => {
    if (!allowed || !mounted.current) return Promise.resolve();
    if (pending.current) return pending.current.promise;
    const owner = { controller: new AbortController(), promise: Promise.resolve() };
    pending.current = owner; attempted.current = Date.now();
    const current = () => mounted.current && active.current.scope === scope && active.current.client === client && pending.current === owner;
    setState(before => ({ ...before, scope, client, data: before.scope === scope && before.client === client ? before.data : undefined, loading: true, error: '' }));
    owner.promise = (async () => {
      try {
        const result = await boundedInstallationRead(client.query({ query, variables: { input: JSON.parse(key) }, fetchPolicy: 'no-cache',
          context: { queryDeduplication: false, fetchOptions: { signal: owner.controller.signal } } }), owner.controller);
        if (result.errors?.length) throw new Error('Unconfirmed monitoring read');
        const data = decode(result.data?.[field], JSON.parse(key));
        if (current()) setState({ scope, client, data, loading: false, error: '' });
      } catch {
        if (current()) setState(before => ({ ...before, loading: false, error: 'Monitoring could not be refreshed. Try again.' }));
      } finally { if (pending.current === owner) pending.current = null; }
    })();
    return owner.promise;
  }, [allowed, client, decode, field, key, query, scope]);
  useEffect(() => {
    mounted.current = true; attempted.current = null;
    const check = () => { if (document.visibilityState !== 'hidden' && installationAutomaticReadDue(attempted.current)) void refresh(); };
    if (allowed) check();
    const timer = allowed ? window.setInterval(check, 60000) : undefined;
    if (allowed) { window.addEventListener('focus', check); document.addEventListener('visibilitychange', check); }
    return () => {
      mounted.current = false; window.clearInterval(timer); window.removeEventListener('focus', check); document.removeEventListener('visibilitychange', check);
      pending.current?.controller.abort(); pending.current = null;
    };
  }, [allowed, refresh]);
  const valid = allowed && state.scope === scope && state.client === client;
  return { data: valid ? state.data : undefined, loading: valid && state.loading, error: valid ? state.error : '', refresh };
}

export function useInstallationMonitoring(storesInput: MonitoringStoresInput, eventsInput: MonitoringEventsInput) {
  const { user } = useUser(); const actor = user?.id || '';
  const allowed = user?.defaultRole === 'shipaid_admin' && user.disabled !== true && assistanceUuid(actor);
  const storesAllowed = allowed && validMonitoringInput(storesInput), eventsAllowed = allowed && validMonitoringInput(eventsInput, true);
  const stores = useMonitoringRead(storesAllowed, actor, storesInput, GET_INSTALLATION_MONITORING_STORES, 'installationMonitoringStores', readMonitoringStores);
  const events = useMonitoringRead(eventsAllowed, actor, eventsInput, GET_INSTALLATION_MONITORING_EVENTS, 'installationMonitoringEvents', readMonitoringEvents);
  return { allowed, stores, events, refresh: () => Promise.all([stores.refresh(), events.refresh()]) };
}
